import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';

// 친구 상태: none, requested, pending, friend
export function useFriendActions(user) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);

  // 친구 목록 로드
  useEffect(() => {
    const loadFriends = async () => {
      if (!user?.id) {
        setFriends([]);
        return;
      }
      try {
        const q = query(collection(db, 'friends'), where('userId', '==', user.id));
        const snap = await getDocs(q);
        const friendIds = snap.docs.map(doc => doc.data().friendId);
        
        if (friendIds.length === 0) {
          setFriends([]);
          return;
        }
        
        // 친구 정보 가져오기
        const usersRef = collection(db, 'users');
        const userQuery = query(usersRef, where('id', 'in', friendIds));
        const userSnap = await getDocs(userQuery);
        const friendsList = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFriends(friendsList);
      } catch (error) {
        console.error('친구 목록 로드 실패:', error);
        setFriends([]);
      }
    };
    
    loadFriends();
  }, [user?.id]);

  // 친구 요청 목록 로드
  useEffect(() => {
    const loadFriendRequests = async () => {
      if (!user?.id) {
        setFriendRequests([]);
        return;
      }
      try {
        const q = query(collection(db, 'friendRequests'), where('to', '==', user.id), where('status', '==', 'pending'));
        const snap = await getDocs(q);
        const requests = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFriendRequests(requests);
      } catch (error) {
        console.error('친구 요청 목록 로드 실패:', error);
        setFriendRequests([]);
      }
    };
    
    loadFriendRequests();
  }, [user?.id]);

  // 친구 상태 확인
  const getFriendStatus = async (targetId) => {
    if (!user || !targetId) return 'none';
    // 1. 내가 요청한 경우
    const q1 = query(collection(db, 'friends'), where('userId', '==', user.id), where('friendId', '==', targetId));
    const snap1 = await getDocs(q1);
    if (!snap1.empty) return 'friend';
    // 2. 상대가 나에게 요청한 경우
    const q2 = query(collection(db, 'friends'), where('userId', '==', targetId), where('friendId', '==', user.id));
    const snap2 = await getDocs(q2);
    if (!snap2.empty) return 'friend';
    // TODO: 요청/수락 대기 상태 구현 필요시 확장
    return 'none';
  };

  // 친구 추가
  const requestFriend = async (targetId) => {
    setLoading(true); setMsg("");
    try {
      if (!user || !targetId) throw new Error('로그인 필요');
      if (user.id === targetId) throw new Error('자신을 추가할 수 없습니다.');
      // 이미 친구인지 확인
      const status = await getFriendStatus(targetId);
      if (status === 'friend') throw new Error('이미 친구입니다.');
      // 쌍방 추가
      await addDoc(collection(db, 'friends'), { userId: user.id, friendId: targetId });
      await addDoc(collection(db, 'friends'), { userId: targetId, friendId: user.id });
      setMsg('친구가 추가되었습니다.');
      return true;
    } catch (e) {
      setMsg(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 친구 요청 수락
  const acceptFriend = async (requestId) => {
    setLoading(true); setMsg("");
    try {
      const requestRef = doc(db, 'friendRequests', requestId);
      const requestSnap = await getDocs(requestRef);
      if (!requestSnap.exists()) throw new Error('요청을 찾을 수 없습니다.');
      
      const requestData = requestSnap.data();
      const fromId = requestData.from;
      const toId = requestData.to;
      
      // 친구 관계 생성
      await addDoc(collection(db, 'friends'), { userId: fromId, friendId: toId });
      await addDoc(collection(db, 'friends'), { userId: toId, friendId: fromId });
      
      // 요청 상태 업데이트
      await updateDoc(requestRef, { status: 'accepted' });
      
      setMsg('친구 요청을 수락했습니다.');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  };

  // 친구 삭제
  const removeFriend = async (targetId) => {
    setLoading(true); setMsg("");
    try {
      // 나-상대, 상대-나 모두 삭제
      const q1 = query(collection(db, 'friends'), where('userId', '==', user.id), where('friendId', '==', targetId));
      const snap1 = await getDocs(q1);
      snap1.forEach(docu => deleteDoc(doc(db, 'friends', docu.id)));
      const q2 = query(collection(db, 'friends'), where('userId', '==', targetId), where('friendId', '==', user.id));
      const snap2 = await getDocs(q2);
      snap2.forEach(docu => deleteDoc(doc(db, 'friends', docu.id)));
      setMsg('친구가 삭제되었습니다.');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, msg, setMsg,
    friends, friendRequests,
    getFriendStatus,
    requestFriend,
    acceptFriend,
    removeFriend
  };
} 